import React from 'react';

import AvatarSkeleton from '@atlaskit/avatar/skeleton';
import { cssMap, cx } from '@atlaskit/css';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { Card } from '@atlassian/studio-browse-kit/ui/card';
import { CardCover } from '@atlassian/studio-browse-kit/ui/card-cover';
import { CardFooter } from '@atlassian/studio-browse-kit/ui/card-footer';

import { ExternalAgentAvatarFrame } from './ExternalAgentAvatarFrame';

const styles = cssMap({
	card: { width: '220px' },
	content: {
		gridArea: 'content',
		alignSelf: 'start',
		minWidth: 0,
	},
	legacyContent: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	titleLine: { display: 'flex', alignItems: 'center', height: '24px' },
	legacyTitleLine: { height: '20px' },
	textLine: { display: 'flex', alignItems: 'center', height: '16px' },
	attribution: { width: '100%' },
	attributionRow: { minWidth: 0, height: '24px' },
});

export type ExternalAgentProfileCardSkeletonProps = {
	/** Rows to reserve: none, invoking person only, or invoking person and channel. */
	attributionRows?: 0 | 1 | 2;
	hasVendorLine?: boolean;
	hasDescription?: boolean;
	testId?: string;
};

const LoadingAvatar = (): React.JSX.Element => {
	if (!expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
		return <AvatarSkeleton size="small" appearance="hexagon" color={token('color.text')} />;
	}

	return (
		<ExternalAgentAvatarFrame>
			<AvatarSkeleton size="xlarge" appearance="hexagon" color={token('color.text')} />
		</ExternalAgentAvatarFrame>
	);
};

const AttributionRowSkeleton = ({
	width,
	testId,
}: {
	width: string;
	testId?: string;
}): React.JSX.Element => (
	<Inline xcss={styles.attributionRow} space="space.075" alignBlock="center" testId={testId}>
		<Skeleton width={16} height={16} borderRadius={token('radius.full')} />
		<Skeleton width={width} height={12} />
	</Inline>
);

/** Reserves the shared agent card layout while the external AppUser data loads. */
export const ExternalAgentProfileCardSkeleton = ({
	attributionRows = 0,
	hasVendorLine = false,
	hasDescription = false,
	testId,
}: ExternalAgentProfileCardSkeletonProps): React.JSX.Element => {
	const isUplift = expVal('jira_browse_agent_modal_update', 'isEnabled', false);

	return (
		<Box xcss={styles.card} testId={testId} aria-hidden="true">
			<Card hasCutout isDisabled>
				<CardCover
					name=""
					avatar={LoadingAvatar}
					testId={testId ? `${testId}--cover` : undefined}
				/>
				<Stack
					xcss={cx(styles.content, !isUplift && styles.legacyContent)}
					space={isUplift ? 'space.150' : 'space.075'}
					testId={testId ? `${testId}--content` : undefined}
				>
					<Stack>
						<Box xcss={cx(styles.titleLine, !isUplift && styles.legacyTitleLine)}>
							<Skeleton width="70%" height={16} />
						</Box>
						{hasVendorLine && isUplift && (
							<Box xcss={styles.textLine}>
								<Skeleton width="45%" height={12} />
							</Box>
						)}
					</Stack>
					{hasDescription && (
						<Box testId={testId ? `${testId}--description` : undefined}>
							<Stack space="space.0">
								<Box xcss={styles.textLine}>
									<Skeleton width="100%" height={12} />
								</Box>
								<Box xcss={styles.textLine}>
									<Skeleton width="90%" height={12} />
								</Box>
							</Stack>
						</Box>
					)}
				</Stack>
				{attributionRows > 0 && (
					<CardFooter>
						<Stack xcss={styles.attribution} testId={testId ? `${testId}--attribution` : undefined}>
							<AttributionRowSkeleton
								width="60%"
								testId={testId ? `${testId}--attribution--invoked-by` : undefined}
							/>
							{attributionRows > 1 && (
								<AttributionRowSkeleton
									width="50%"
									testId={testId ? `${testId}--attribution--invoked-via` : undefined}
								/>
							)}
						</Stack>
					</CardFooter>
				)}
			</Card>
		</Box>
	);
};
